import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { FeatureKey, PermissionKey } from '@lkq/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LoadingState } from '@/components/ui/states';

// SPA route guards (docs/02 enforcement layers) — courtesy layer; the API
// middleware is the real boundary.

export function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <LoadingState />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <Outlet />;
}

/** Garage users only; routes by membership status (docs/06 access effects). */
export function MemberGuard() {
  const { me } = useAuth();
  if (!me) return null;
  if (!me.user.garageId) {
    return <Navigate to={me.user.isPlatformAdmin ? '/admin' : '/concept'} replace />;
  }
  if (me.user.membershipStatus === 'pending') return <Navigate to="/onboarding" replace />;
  // Archive is a separate flag that can co-exist with an 'active' status, so it
  // is checked explicitly before the active gate (docs/06 — archived blocks like expired).
  if (me.user.isArchived) return <Navigate to="/membership-unavailable?status=archived" replace />;
  if (me.user.membershipStatus !== 'active') {
    return <Navigate to={`/membership-unavailable?status=${me.user.membershipStatus}`} replace />;
  }
  return <Outlet />;
}

/** Onboarding wizard is only for a garage user whose membership is still pending;
 *  anyone else (active, blocked, admin) is routed home so they can't re-run it. */
export function OnboardingGuard() {
  const { me } = useAuth();
  if (!me) return null;
  if (!me.user.garageId) return <Navigate to={me.user.isPlatformAdmin ? '/admin' : '/concept'} replace />;
  if (me.user.membershipStatus !== 'pending') return <Navigate to="/" replace />;
  return <Outlet />;
}

export function ConceptAdminGuard() {
  const { me } = useAuth();
  if (!me) return null;
  if (!me.user.isPlatformAdmin && me.user.adminConceptIds.length === 0) return <Navigate to="/" replace />;
  return <Outlet />;
}

export function SystemAdminGuard() {
  const { me } = useAuth();
  if (!me) return null;
  if (!me.user.isPlatformAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Feature-flag guard: disabled feature → 404 page (docs/10 — "doesn't exist"). */
export function FeatureGuard({ feature, children }: { feature: FeatureKey; children: ReactNode }) {
  const { features } = useTheme();
  if (!features[feature]) return <Navigate to="/not-found" replace />;
  return <>{children}</>;
}

/** Permission guard: hides pages the role can't use (courtesy; API still enforces). */
export function PermissionGuard({ permission, children }: { permission: PermissionKey; children: ReactNode }) {
  const { hasPermission } = useAuth();
  if (!hasPermission(permission)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
